import { Container, Label, TextInput, TextAreaInput, Element as PcuiElement } from '@playcanvas/pcui';

import { Events } from '../events';
import deleteSvg from './svg/delete.svg';

const createSvg = (svgString: string) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement;
};

// list of annotations shown in the scene panel. rebuilds from the annotation
// state whenever it changes, and reflects/drives selection. ctrl/cmd/shift-click
// a row to multi-select (mirrors the splat list); a single selection shows an
// inline edit panel (text, link, image, curated camera view) below its row,
// a multi-selection shows a bulk-delete bar instead.
class AnnotationList extends Container {
    constructor(events: Events, args = {}) {
        args = {
            ...args,
            class: 'splat-list'
        };

        super(args);

        const edit = new TextInput({ id: 'annotation-edit' });

        // build the inline editor (text / link / image / camera) for the selected annotation
        const buildEditor = (annotation: any) => {
            const editor = new Container({ class: 'annotation-editor' });

            const textLabel = new Label({ class: 'annotation-editor-label', text: 'Description' });
            const textInput = new TextAreaInput({
                class: 'annotation-editor-text',
                value: annotation.text ?? ''
            });
            textInput.on('change', (value: string) => {
                events.fire('annotations.update', annotation.id, { text: value });
            });

            const linkLabel = new Label({ class: 'annotation-editor-label', text: 'Link (optional)' });
            const linkInput = new TextInput({
                class: 'annotation-editor-link',
                placeholder: 'https://…',
                value: annotation.link ?? ''
            });
            linkInput.on('change', (value: string) => {
                events.fire('annotations.update', annotation.id, { link: value });
            });

            const imageLabel = new Label({ class: 'annotation-editor-label', text: 'Image (optional)' });
            const imageRow = new Container({ class: 'annotation-editor-image-row' });

            const renderImageRow = () => {
                imageRow.clear();

                if (annotation.image) {
                    const thumb = document.createElement('img');
                    thumb.className = 'annotation-editor-thumb';
                    thumb.src = annotation.image;
                    imageRow.dom.appendChild(thumb);

                    const removeBtn = new PcuiElement({
                        dom: createSvg(deleteSvg),
                        class: 'splat-item-delete'
                    });
                    removeBtn.dom.addEventListener('click', () => {
                        annotation.image = '';
                        events.fire('annotations.update', annotation.id, { image: '' });
                        renderImageRow();
                    });
                    imageRow.append(removeBtn);
                } else {
                    const addBtn = new Label({ class: 'annotation-editor-add-image', text: '+ Add image' });
                    addBtn.dom.addEventListener('click', () => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.addEventListener('change', () => {
                            const file = input.files?.[0];
                            if (!file) {
                                return;
                            }
                            const reader = new FileReader();
                            reader.onload = () => {
                                const dataUrl = reader.result as string;
                                annotation.image = dataUrl;
                                events.fire('annotations.update', annotation.id, { image: dataUrl });
                                renderImageRow();
                            };
                            reader.readAsDataURL(file);
                        });
                        input.click();
                    });
                    imageRow.append(addBtn);
                }
            };
            renderImageRow();

            // curated camera view: "Save current view" captures the live camera
            // pose onto this hotspot so clicking it later snaps to that exact
            // framing instead of just re-centering on its position.
            const cameraLabel = new Label({ class: 'annotation-editor-label', text: 'Camera view' });
            const cameraRow = new Container({ class: 'annotation-editor-camera-row' });

            const renderCameraRow = () => {
                cameraRow.clear();

                if (annotation.camera) {
                    const status = new Label({ class: 'annotation-editor-camera-status', text: '✓ Custom view saved' });
                    cameraRow.append(status);

                    const updateBtn = new Label({ class: 'annotation-editor-camera-link', text: 'Update' });
                    updateBtn.dom.addEventListener('click', () => {
                        const pose = events.invoke('camera.getPose');
                        events.fire('annotations.update', annotation.id, {
                            camera: { position: [pose.position.x, pose.position.y, pose.position.z], target: [pose.target.x, pose.target.y, pose.target.z], fov: pose.fov }
                        });
                        annotation.camera = { position: [pose.position.x, pose.position.y, pose.position.z], target: [pose.target.x, pose.target.y, pose.target.z], fov: pose.fov };
                        renderCameraRow();
                    });
                    cameraRow.append(updateBtn);

                    const clearBtn = new Label({ class: 'annotation-editor-camera-link', text: 'Clear' });
                    clearBtn.dom.addEventListener('click', () => {
                        events.fire('annotations.update', annotation.id, { camera: null });
                        annotation.camera = null;
                        renderCameraRow();
                    });
                    cameraRow.append(clearBtn);
                } else {
                    const saveBtn = new Label({ class: 'annotation-editor-add-image', text: '+ Save current view' });
                    saveBtn.dom.addEventListener('click', () => {
                        const pose = events.invoke('camera.getPose');
                        const camera = { position: [pose.position.x, pose.position.y, pose.position.z] as [number, number, number], target: [pose.target.x, pose.target.y, pose.target.z] as [number, number, number], fov: pose.fov };
                        events.fire('annotations.update', annotation.id, { camera });
                        annotation.camera = camera;
                        renderCameraRow();
                    });
                    cameraRow.append(saveBtn);
                }
            };
            renderCameraRow();

            editor.append(textLabel);
            editor.append(textInput);
            editor.append(linkLabel);
            editor.append(linkInput);
            editor.append(imageLabel);
            editor.append(imageRow);
            editor.append(cameraLabel);
            editor.append(cameraRow);

            // stop clicks inside the editor from bubbling to the row (which selects)
            editor.dom.addEventListener('click', (e) => e.stopPropagation());
            editor.dom.addEventListener('pointerdown', (e) => e.stopPropagation());

            return editor;
        };

        // bulk toolbar shown instead of the inline editor when multiple rows are selected
        const buildBulkBar = (count: number) => {
            const bar = new Container({ class: 'annotation-bulk-bar' });
            const label = new Label({ class: 'annotation-bulk-label', text: `${count} hotspots selected` });
            const deleteBtn = new PcuiElement({
                dom: createSvg(deleteSvg),
                class: 'splat-item-delete'
            });
            deleteBtn.dom.addEventListener('click', (e) => {
                e.stopPropagation();
                events.fire('annotations.deleteSelected');
            });
            bar.append(label);
            bar.append(deleteBtn);
            bar.dom.addEventListener('click', (e) => e.stopPropagation());
            return bar;
        };

        const rebuild = () => {
            this.clear();

            const list = events.invoke('annotations.list') ?? [];
            const selectedId = events.invoke('annotations.selected');
            const selectedIds: number[] = events.invoke('annotations.selectedIds') ?? [];
            const selectedSet = new Set(selectedIds);
            const isMulti = selectedSet.size > 1;

            if (list.length === 0) {
                const empty = new Label({
                    class: 'annotation-empty',
                    text: 'No annotations yet. Click + to place one.'
                });
                this.append(empty);
                return;
            }

            if (isMulti) {
                this.append(buildBulkBar(selectedSet.size));
            }

            list.forEach((annotation: any, index: number) => {
                const isSelected = selectedSet.has(annotation.id);
                const item = new Container({
                    class: isSelected ? ['splat-item', 'selected'] : ['splat-item']
                });

                const text = new Label({
                    class: 'splat-item-text',
                    text: `${index + 1}. ${annotation.title}`
                });

                const remove = new PcuiElement({
                    dom: createSvg(deleteSvg),
                    class: 'splat-item-delete'
                });

                item.append(text);
                item.append(remove);

                item.dom.addEventListener('click', (event: MouseEvent) => {
                    if (event.shiftKey || event.ctrlKey || event.metaKey) {
                        events.fire('annotations.toggleSelect', annotation.id);
                    } else {
                        events.fire('annotations.select', annotation.id);
                        events.fire('annotations.flyTo', annotation.id);
                    }
                });

                // rename on double click
                text.dom.addEventListener('dblclick', (event: MouseEvent) => {
                    event.stopPropagation();

                    const onblur = () => {
                        item.remove(edit);
                        events.fire('annotations.update', annotation.id, { title: edit.value });
                        edit.input.removeEventListener('blur', onblur);
                        text.hidden = false;
                    };

                    text.hidden = true;
                    item.appendAfter(edit, text);
                    edit.value = annotation.title;
                    edit.input.addEventListener('blur', onblur);
                    edit.focus();
                });

                remove.dom.addEventListener('click', (event: MouseEvent) => {
                    event.stopPropagation();
                    events.fire('annotations.remove', annotation.id);
                });

                this.append(item);

                if (!isMulti && annotation.id === selectedId) {
                    this.append(buildEditor(annotation));
                }
            });
        };

        events.on('annotations.changed', rebuild);
        events.on('annotations.selectionChanged', rebuild);

        rebuild();
    }
}

export { AnnotationList };
